// Org-wide pending MC (medical certificate) count shared by the app-sidebar "Work Plan"
// pip and the planner's Confirm-MC button. Live-updated by workplan.* WS events (which
// carry the fresh count), refreshed on login and on a slow interval as the fallback.

import { fetchMCRequests } from '@/lib/api/work-plan';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { subscribeWorkPlanEvents } from '@/lib/realtime/chat-realtime-service';
import { hasPermission } from '@/lib/stores/permissions-store';

const REFRESH_INTERVAL_MS = 120000;

let pendingCount = 0;
let started = false;
const listeners = new Set();

function emit() {
  for (const listener of listeners) {
    try {
      listener(pendingCount);
    } catch {
      /* ignore listener failures */
    }
  }
}

function setCount(next) {
  const value = Number.isFinite(Number(next)) ? Math.max(0, Number(next)) : 0;
  if (value === pendingCount) return;
  pendingCount = value;
  emit();
}

export async function refreshPendingMCCount() {
  if (!getAuthToken()) {
    setCount(0);
    return 0;
  }
  // The count endpoint needs work_plan:view — skip quietly for users without it
  // (their sidebar hides the Work Plan entry anyway). Must stay in step with the backend:
  // the MC queue moved off `scheduling` when work_plan was carved out, so polling on the old
  // module would 403 on every tick for every scheduling-only user.
  if (!hasPermission('work_plan', 'view')) {
    setCount(0);
    return 0;
  }
  const requestUserId = String(getAuthUser()?.id || '').trim();
  try {
    const response = await fetchMCRequests({ status: 'pending' });
    if (String(getAuthUser()?.id || '').trim() !== requestUserId) {
      return pendingCount;
    }
    setCount(response?.pendingCount ?? (Array.isArray(response?.items) ? response.items.length : 0));
  } catch {
    /* keep the previous count; the next tick retries */
  }
  return pendingCount;
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshPendingMCCount();
  // Never cleared — the store lives for the app session, like the other module stores.
  setInterval(() => void refreshPendingMCCount(), REFRESH_INTERVAL_MS);
  subscribeAuthStateChange(() => void refreshPendingMCCount());
  subscribeWorkPlanEvents((detail) => {
    // mc_submitted / mc_decided carry the fresh org-wide count — no fetch needed.
    if (detail?.pendingMcCount !== null && detail?.pendingMcCount !== undefined) {
      setCount(detail.pendingMcCount);
      return;
    }
    if (String(detail?.action || '').startsWith('mc_')) {
      void refreshPendingMCCount();
    }
  });
}

/** Last known pending MC count (0 until the first successful fetch). */
export function getPendingMCCount() {
  ensureStarted();
  return pendingCount;
}

export function subscribePendingMCCount(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
